import React, { useEffect, useMemo, useState } from 'react';
import { Select, Tag, Space, Spin, message, Tooltip } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { updateTags } from '../../actions/tagsActions';

const EditableTags = ({ order, onSave, resource = 'vendor_orders' }) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(order.tag_list || []);
  const availableTags = useSelector((state) => {
    const orderTags = (state.orders && state.orders.availableTags) || [];
    const quoteTags = (state.quotes && state.quotes.availableTags) || [];
    return Array.from(new Set([...(orderTags || []), ...(quoteTags || [])]));
  });

  useEffect(() => {
    setValue(order.tag_list || []);
  }, [order.id, order.tag_list]);

  const normalized = useMemo(() => {
    const trimmed = (value || []).map((t) => String(t).trim()).filter((t) => t.length > 0);
    return Array.from(new Set(trimmed));
  }, [value]);

  const originalNormalized = useMemo(() => {
    const v = (order.tag_list || []).map((t) => String(t).trim()).filter((t) => t.length > 0);
    return Array.from(new Set(v));
  }, [order.tag_list]);

  const isChanged = useMemo(() => {
    if (normalized.length !== originalNormalized.length) return true;
    const a = [...normalized].sort();
    const b = [...originalNormalized].sort();
    return a.some((t, i) => t !== b[i]);
  }, [normalized, originalNormalized]);

  const handleSave = async () => {
    if (!isChanged) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      const updated = await dispatch(updateTags({ resource, id: order.id, tagList: normalized }));
      if (onSave) onSave(updated);
      message.success('Tags updated');
      setIsEditing(false);
    } catch (err) {
      message.error('Failed to update tags');
      setValue(order.tag_list || []);
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const renderView = () => {
    const tags = order.tag_list || [];
    if (!tags.length) {
      return (
        <Space
          size="small"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' ? setIsEditing(true) : null)}
          onClick={() => setIsEditing(true)}
          style={{ cursor: 'pointer', color: '#999' }}
        >
          <span>Add tags</span>
          <Tooltip title="Add, remove, or create tags">
            <PlusCircleOutlined
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' ? setIsEditing(true) : null)}
              onClick={() => setIsEditing(true)}
              style={{ color: 'rgba(0,0,0,0.45)' }}
            />
          </Tooltip>
        </Space>
      );
    }
    return (
      <Space
        size="small"
        wrap
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' ? setIsEditing(true) : null)}
        onClick={() => setIsEditing(true)}
        style={{ cursor: 'pointer' }}
      >
        {tags.map((tag) => (
          <Tag key={tag}>{tag}</Tag>
        ))}
        <Tooltip title="Add, remove, or create tags">
          <PlusCircleOutlined
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' ? setIsEditing(true) : null)}
            onClick={() => setIsEditing(true)}
            style={{ color: 'rgba(0,0,0,0.45)' }}
          />
        </Tooltip>
      </Space>
    );
  };

  return (
    <div>
      {isEditing ? (
        <div style={{ width: '100%' }}>
          <Select
            mode="tags"
            value={value}
            onChange={setValue}
            onBlur={handleSave}
            autoFocus
            size="small"
            style={{ width: '100%' }}
            placeholder="Add tags"
            showSearch
            maxTagCount="responsive"
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 280 }}
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label || '').toLowerCase().includes((input || '').toLowerCase())
            }
            options={(availableTags || []).map((name) => ({ value: name, label: name }))}
          />
          {saving && <Spin size="small" style={{ marginLeft: 8 }} />}
        </div>
      ) : (
        renderView()
      )}
    </div>
  );
};

export default EditableTags;
